import type { ReactElement } from "react";

/**
 * Thin-line jewelry-domain SVG icons.
 * All icons share a 56×56 viewBox, 1–1.5px strokes, same palette.
 * Usage: <JewelryIcon type="story-lab" size={48} />
 */

export type JewelryIconType =
  | "story-lab"
  | "ip-lock"
  | "viewer-3d"
  | "workspace"
  | "designer-direct"
  | "customize"
  | "specs"
  | "production"
  | "cad-file"
  | "approval"
  | "workflow"
  | "eco-plate";

const S = "oklch(0.085 0.004 50)";   // India ink
const G = "oklch(0.48 0.072 52)";    // aged gold leaf

const ICONS: Record<JewelryIconType, ReactElement> = {
  /* ── Story Lab: open scroll with ruled lines and quill ── */
  "story-lab": (
    <>
      {/* Scroll body */}
      <rect x="10" y="12" width="36" height="30" rx="2" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.7" />
      {/* Curled scroll ends */}
      <path d="M10 12 C10 8, 46 8, 46 12" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.5" />
      <path d="M10 42 C10 46, 46 46, 46 42" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.5" />
      {/* Text lines */}
      <line x1="16" y1="20" x2="36" y2="20" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      <line x1="16" y1="25" x2="40" y2="25" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      <line x1="16" y1="30" x2="30" y2="30" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      {/* Quill pen */}
      <path d="M36 34 L44 16 C44 16, 38 20, 34 30 Z" fill="none" stroke={G} strokeWidth="1" strokeOpacity="0.7" strokeLinejoin="round" />
      <line x1="36" y1="34" x2="38" y2="38" stroke={G} strokeWidth="1" strokeOpacity="0.6" />
    </>
  ),

  /* ── IP Lock: rounded lock with a diamond inset ── */
  "ip-lock": (
    <>
      {/* Lock body */}
      <rect x="12" y="26" width="32" height="22" rx="3" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.7" />
      {/* Shackle arch */}
      <path d="M18 26 L18 18 C18 12, 38 12, 38 18 L38 26" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.7" />
      {/* Inner shackle hole */}
      <path d="M21 26 L21 19 C21 15, 35 15, 35 19 L35 26" fill="none" stroke={S} strokeWidth="0.7" strokeOpacity="0.3" />
      {/* Diamond inside lock */}
      <path d="M28 31 L24 36 L28 43 L32 36 Z" fill="none" stroke={G} strokeWidth="1" strokeOpacity="0.8" />
      <line x1="24" y1="36" x2="32" y2="36" stroke={G} strokeWidth="0.7" strokeOpacity="0.5" />
    </>
  ),

  /* ── 3D Viewer: isometric cube ── */
  "viewer-3d": (
    <>
      {/* Top face */}
      <path d="M28 8 L46 18 L28 28 L10 18 Z" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.7" />
      {/* Left face */}
      <path d="M10 18 L28 28 L28 46 L10 36 Z" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.6" />
      {/* Right face */}
      <path d="M28 28 L46 18 L46 36 L28 46 Z" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.6" />
      {/* Top face interior */}
      <line x1="28" y1="8" x2="28" y2="28" stroke={S} strokeWidth="0.5" strokeOpacity="0.25" />
      <line x1="10" y1="18" x2="46" y2="18" stroke={S} strokeWidth="0.5" strokeOpacity="0.25" />
      {/* Gold accent on top edge */}
      <path d="M28 8 L46 18 L28 28 L10 18 Z" fill="none" stroke={G} strokeWidth="0.6" strokeOpacity="0.4" />
      {/* Axis marks */}
      <circle cx="10" cy="18" r="1.5" fill={G} opacity="0.6" />
      <circle cx="46" cy="18" r="1.5" fill={G} opacity="0.6" />
      <circle cx="28" cy="8" r="1.5" fill={G} opacity="0.6" />
    </>
  ),

  /* ── Studio Workspace: clipboard / inbox tray ── */
  "workspace": (
    <>
      {/* Main document tray */}
      <rect x="8" y="16" width="40" height="30" rx="2" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.7" />
      {/* Clip at top */}
      <rect x="20" y="10" width="16" height="8" rx="2" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.6" />
      {/* Documents/lines */}
      <line x1="14" y1="25" x2="42" y2="25" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      <line x1="14" y1="30" x2="38" y2="30" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      <line x1="14" y1="35" x2="42" y2="35" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      {/* Gold check mark */}
      <path d="M34 34 L37 38 L44 28" fill="none" stroke={G} strokeWidth="1.3" strokeOpacity="0.75" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),

  /* ── Designer-Direct: D→◆→R (no middleman) ── */
  "designer-direct": (
    <>
      {/* Designer circle (left) */}
      <circle cx="10" cy="28" r="6" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.65" />
      <text x="10" y="32" fontFamily="serif" fontSize="8" fill={S} fillOpacity="0.5" textAnchor="middle">D</text>
      {/* Buyer circle (right) */}
      <circle cx="46" cy="28" r="6" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.65" />
      <text x="46" y="32" fontFamily="serif" fontSize="8" fill={S} fillOpacity="0.5" textAnchor="middle">R</text>
      {/* Direct connection line */}
      <line x1="16" y1="28" x2="22" y2="28" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      <line x1="34" y1="28" x2="40" y2="28" stroke={S} strokeWidth="0.9" strokeOpacity="0.4" />
      {/* Diamond in center */}
      <path d="M28 18 L22 28 L28 38 L34 28 Z" fill="none" stroke={G} strokeWidth="1.2" strokeOpacity="0.8" />
      <line x1="22" y1="28" x2="34" y2="28" stroke={G} strokeWidth="0.7" strokeOpacity="0.45" />
    </>
  ),

  /* ── Customize: diamond with edit pen ── */
  "customize": (
    <>
      {/* Diamond outline */}
      <path d="M28 10 L18 26 L28 46 L38 26 Z" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.6" />
      <line x1="18" y1="26" x2="38" y2="26" stroke={S} strokeWidth="0.8" strokeOpacity="0.35" />
      {/* Crown lines */}
      <line x1="22" y1="26" x2="24" y2="10" stroke={S} strokeWidth="0.6" strokeOpacity="0.25" />
      <line x1="34" y1="26" x2="32" y2="10" stroke={S} strokeWidth="0.6" strokeOpacity="0.25" />
      {/* Pencil / edit pen overlapping */}
      <path d="M38 12 L44 20 L32 42 L26 44 L28 38 Z" fill="none" stroke={G} strokeWidth="1" strokeOpacity="0.75" strokeLinejoin="round" />
      <line x1="38" y1="12" x2="42" y2="16" stroke={G} strokeWidth="0.7" strokeOpacity="0.5" />
      <line x1="28" y1="38" x2="32" y2="42" stroke={G} strokeWidth="0.7" strokeOpacity="0.5" />
    </>
  ),

  /* ── Specs: blueprint grid with ring profile inset ── */
  "specs": (
    <>
      {/* Blueprint document */}
      <rect x="6" y="8" width="44" height="40" rx="2" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.6" />
      {/* Grid lines inside */}
      {[14, 20, 26, 32, 38].map((x) => (
        <line key={`sv${x}`} x1={x} y1="8" x2={x} y2="48" stroke={S} strokeWidth="0.4" strokeOpacity="0.18" />
      ))}
      {[16, 24, 32, 40].map((y) => (
        <line key={`sh${y}`} x1="6" y1={y} x2="50" y2={y} stroke={S} strokeWidth="0.4" strokeOpacity="0.18" />
      ))}
      {/* Ring profile inset */}
      <circle cx="28" cy="28" r="10" fill="none" stroke={G} strokeWidth="1.1" strokeOpacity="0.7" />
      <circle cx="28" cy="28" r="6" fill="none" stroke={G} strokeWidth="0.7" strokeOpacity="0.4" />
      {/* Dimension arrows */}
      <line x1="8" y1="28" x2="16" y2="28" stroke={S} strokeWidth="0.6" strokeOpacity="0.35" />
      <line x1="40" y1="28" x2="48" y2="28" stroke={S} strokeWidth="0.6" strokeOpacity="0.35" />
    </>
  ),

  /* ── Production / EPH: factory hub with eco leaf ── */
  "production": (
    <>
      {/* Factory building */}
      <rect x="10" y="26" width="36" height="22" rx="1" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.65" />
      {/* Roof / chimney stacks */}
      <rect x="14" y="18" width="6" height="10" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.5" />
      <rect x="24" y="14" width="6" height="14" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.5" />
      <rect x="36" y="20" width="6" height="8" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.5" />
      {/* Windows */}
      <rect x="14" y="32" width="6" height="6" fill="none" stroke={S} strokeWidth="0.7" strokeOpacity="0.35" />
      <rect x="25" y="32" width="6" height="6" fill="none" stroke={S} strokeWidth="0.7" strokeOpacity="0.35" />
      {/* Eco leaf (gold accent) */}
      <path d="M39 38 C39 34, 44 30, 48 30 C48 34, 44 38, 40 38 Z" fill="none" stroke={G} strokeWidth="1" strokeOpacity="0.75" />
      <line x1="39" y1="38" x2="44" y2="34" stroke={G} strokeWidth="0.6" strokeOpacity="0.5" />
    </>
  ),

  /* ── CAD File: document with ring drawing inside ── */
  "cad-file": (
    <>
      {/* File with folded corner */}
      <path d="M8 8 L40 8 L48 16 L48 48 L8 48 Z" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.65" />
      <path d="M40 8 L40 16 L48 16" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.5" />
      {/* Ring side-profile inside the file */}
      <path d="M16 30 C16 26, 18 24, 20 24 L36 24 C38 24, 40 26, 40 30 C40 34, 38 36, 36 36 L20 36 C18 36, 16 34, 16 30 Z"
        fill="none" stroke={G} strokeWidth="1" strokeOpacity="0.65" />
      <path d="M20 24 C20 22, 20 20, 20 20 C20 20, 36 20, 36 20 C36 20, 36 22, 36 24"
        fill="none" stroke={G} strokeWidth="0.7" strokeOpacity="0.4" />
      {/* Dimension lines */}
      <line x1="14" y1="40" x2="42" y2="40" stroke={S} strokeWidth="0.5" strokeDasharray="2 3" strokeOpacity="0.3" />
    </>
  ),

  /* ── Approval: circular stamp / seal with checkmark ── */
  "approval": (
    <>
      {/* Outer seal serrations */}
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * 22.5 * Math.PI) / 180;
        const r1 = 20, r2 = 23;
        return (
          <line
            key={i}
            x1={28 + r1 * Math.cos(a)} y1={28 + r1 * Math.sin(a)}
            x2={28 + r2 * Math.cos(a)} y2={28 + r2 * Math.sin(a)}
            stroke={S} strokeWidth="1" strokeOpacity="0.5"
          />
        );
      })}
      {/* Circle */}
      <circle cx="28" cy="28" r="17" fill="none" stroke={S} strokeWidth="1.1" strokeOpacity="0.65" />
      {/* Check mark */}
      <path d="M19 28 L25 35 L38 20" fill="none" stroke={G} strokeWidth="1.6" strokeOpacity="0.85" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),

  /* ── Workflow: 4 connected phase boxes ── */
  "workflow": (
    <>
      {/* Phase boxes */}
      <rect x="4" y="16" width="14" height="12" rx="1.5" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.6" />
      <rect x="21" y="16" width="14" height="12" rx="1.5" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.6" />
      <rect x="38" y="16" width="14" height="12" rx="1.5" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.6" />
      {/* Row 2 */}
      <rect x="4" y="34" width="14" height="12" rx="1.5" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.55" />
      <rect x="21" y="34" width="14" height="12" rx="1.5" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.55" />
      <rect x="38" y="34" width="14" height="12" rx="1.5" fill="none" stroke={G} strokeWidth="1" strokeOpacity="0.7" />
      {/* Arrows — row 1 */}
      <line x1="18" y1="22" x2="21" y2="22" stroke={S} strokeWidth="0.8" strokeOpacity="0.4" />
      <line x1="35" y1="22" x2="38" y2="22" stroke={S} strokeWidth="0.8" strokeOpacity="0.4" />
      {/* Arrow — right column down */}
      <line x1="45" y1="28" x2="45" y2="34" stroke={S} strokeWidth="0.8" strokeOpacity="0.4" />
      {/* Arrows — row 2 reversed */}
      <line x1="35" y1="40" x2="38" y2="40" stroke={S} strokeWidth="0.8" strokeOpacity="0.4" />
      <line x1="18" y1="40" x2="21" y2="40" stroke={S} strokeWidth="0.8" strokeOpacity="0.4" />
      {/* Gold tick in final box */}
      <path d="M42 40 L45 44 L50 36" fill="none" stroke={G} strokeWidth="1.2" strokeOpacity="0.8" strokeLinecap="round" />
    </>
  ),

  /* ── Eco Plate: atom / molecular ring structure ── */
  "eco-plate": (
    <>
      {/* Central atom */}
      <circle cx="28" cy="28" r="4" fill="none" stroke={G} strokeWidth="1.2" strokeOpacity="0.8" />
      {/* Orbital rings */}
      <ellipse cx="28" cy="28" rx="18" ry="8" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.4"
        transform="rotate(-30 28 28)" />
      <ellipse cx="28" cy="28" rx="18" ry="8" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.4"
        transform="rotate(30 28 28)" />
      <ellipse cx="28" cy="28" rx="18" ry="8" fill="none" stroke={S} strokeWidth="1" strokeOpacity="0.4"
        transform="rotate(90 28 28)" />
      {/* Outer orbit */}
      <circle cx="28" cy="28" r="22" fill="none" stroke={S} strokeWidth="0.5" strokeDasharray="2 4" strokeOpacity="0.25" />
      {/* Orbital nodes */}
      {[0, 60, 120, 180, 240, 300].map((deg) => {
        const a = (deg * Math.PI) / 180;
        return <circle key={deg} cx={28 + 22 * Math.cos(a)} cy={28 + 22 * Math.sin(a)} r="1.5" fill={G} opacity="0.5" />;
      })}
    </>
  ),
};

export function JewelryIcon({
  type,
  size = 48,
  className = "",
}: {
  type: JewelryIconType;
  size?: number;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 56 56"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      {ICONS[type]}
    </svg>
  );
}
